import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Lock, Activity, Users, Gamepad2, Trophy, Zap, Target, Flame,
  TrendingUp, UserPlus, RefreshCw, ArrowLeft, Radio, Megaphone
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import BirdAvatar from '../components/BirdAvatar';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  ComposedChart, Line, Legend, CartesianGrid
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const REACTION_EMOJI = {
  thumbs_up: '👍', thumbs_down: '👎', heart: '❤️',
  fire: '🔥', eggplant: '🍆', splash: '💦',
};
const REACTION_LABEL = {
  thumbs_up: 'Thumbs Up', thumbs_down: 'Thumbs Down', heart: 'Heart',
  fire: 'Fire', eggplant: 'Eggplant', splash: 'Splash',
};

const proxyUrl = (url) => {
  if (!url) return '';
  if (url.includes('mecklenburgcountync.gov')) {
    return `${API}/api/mugshots/image-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const relTime = (s) => {
  if (s == null) return '';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

const Metric = ({ icon: Icon, label, value, sub, accent = 'text-primary' }) => (
  <div className="p-4 rounded-xl bg-card/60 border border-border">
    <div className="flex items-center gap-2 mb-1.5">
      <Icon className={`w-4 h-4 ${accent}`} />
      <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
    </div>
    <div className="text-2xl font-heading font-bold tabular-nums">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const PasswordGate = ({ onUnlock }) => {
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/analytics/rta?password=${encodeURIComponent(pw)}`);
      if (res.ok) {
        sessionStorage.setItem('rta_pw', pw);
        onUnlock(pw);
      } else {
        setError('Incorrect password.');
      }
    } catch {
      setError('Could not reach the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <Card className="p-8 bg-gradient-card border-border text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/15 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-xl font-heading font-bold mb-1">Real-Time Analytics</h1>
          <p className="text-sm text-muted-foreground mb-6">Admin access only</p>
          <form onSubmit={submit} className="space-y-3">
            <Input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Enter admin password"
              data-testid="rta-password-input"
              autoFocus
            />
            {error && <p className="text-sm text-destructive" data-testid="rta-error">{error}</p>}
            <Button type="submit" disabled={loading || !pw} className="w-full bg-gradient-primary text-primary-foreground font-semibold" data-testid="rta-unlock-btn">
              {loading ? 'Checking…' : 'Unlock Dashboard'}
            </Button>
          </form>
        </Card>
      </motion.div>
    </div>
  );
};

const RTAPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState(sessionStorage.getItem('rta_pw') || '');
  const [unlocked, setUnlocked] = useState(!!sessionStorage.getItem('rta_pw'));
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async (pw) => {
    try {
      const res = await fetch(`${API}/api/analytics/rta?password=${encodeURIComponent(pw)}`);
      if (res.status === 401) {
        sessionStorage.removeItem('rta_pw');
        setUnlocked(false);
        return;
      }
      const d = await res.json();
      if (d.success) {
        setData(d);
        setUpdatedAt(new Date());
      }
    } catch { /* keep last data */ }
  }, []);

  useEffect(() => {
    if (!unlocked || !password) return;
    load(password);
    timer.current = setInterval(() => load(password), 8000);
    return () => clearInterval(timer.current);
  }, [unlocked, password, load]);

  if (!unlocked) {
    return <PasswordGate onUnlock={(pw) => { setPassword(pw); setUnlocked(true); }} />;
  }

  const live = data?.live || {};
  const today = data?.today || {};
  const ret = data?.retention || {};
  const hourly = data?.games_per_hour || [];
  const maxHour = Math.max(1, ...hourly.map((h) => h.games));

  return (
    <div className="min-h-screen bg-background" data-testid="rta-dashboard">
      <div className="container mx-auto max-w-6xl px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-muted-foreground">
              <ArrowLeft className="w-4 h-4 mr-1" /> Home
            </Button>
            <h1 className="text-2xl font-heading font-bold">Real-Time Analytics</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-xs text-green-400 font-semibold">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
              LIVE
            </span>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {updatedAt ? `Updated ${updatedAt.toLocaleTimeString()}` : 'Loading…'}
            </span>
            <Button
              variant="outline" size="sm" onClick={() => navigate('/ads-analytics')}
              className="border-primary/40 text-primary hover:bg-primary/10"
              data-testid="rta-ad-analytics-link"
            >
              <Megaphone className="w-4 h-4 mr-1.5" /> Ad Analytics
            </Button>
            <Button variant="outline" size="sm" onClick={() => load(password)} data-testid="rta-refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {!data ? (
          <div className="text-center py-20 text-muted-foreground">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent mx-auto mb-3" />
            Loading analytics…
          </div>
        ) : (
          <div className="space-y-6">
            {/* Live presence highlight */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-5 bg-gradient-to-br from-green-500/20 to-emerald-600/5 border-green-500/30">
                <div className="flex items-center gap-2 mb-1 text-green-400">
                  <Radio className="w-4 h-4" /><span className="text-xs uppercase tracking-wide">Online Now</span>
                </div>
                <div className="text-4xl font-heading font-bold tabular-nums" data-testid="rta-online-now">{live.online_now}</div>
              </Card>
              <Card className="p-5 bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30">
                <div className="flex items-center gap-2 mb-1 text-primary">
                  <Gamepad2 className="w-4 h-4" /><span className="text-xs uppercase tracking-wide">Playing Now</span>
                </div>
                <div className="text-4xl font-heading font-bold tabular-nums">{live.in_game_now}</div>
              </Card>
              <Metric icon={Users} label="Active Today" value={today.active_players} accent="text-cyan-400" />
              <Metric icon={Gamepad2} label="Games Today" value={today.games} accent="text-amber-400" />
            </div>

            {/* Most-reacted mugshot of the day */}
            {data.mugshot_of_day && (
              <Card className="p-4 bg-gradient-to-r from-orange-500/15 to-red-500/5 border-orange-500/30" data-testid="rta-mugshot-of-day">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-lg overflow-hidden border border-orange-500/40 bg-muted">
                    <img src={proxyUrl(data.mugshot_of_day.mugshot_url)} alt={data.mugshot_of_day.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wide text-orange-400 font-semibold flex items-center gap-1 mb-0.5">
                      <Flame className="w-3.5 h-3.5" /> Most-Reacted Mugshot Today
                    </div>
                    <div className="font-heading font-bold text-lg truncate">{data.mugshot_of_day.name}</div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-sm font-semibold text-orange-300">{data.mugshot_of_day.total_reactions} reactions</span>
                      <span className="flex items-center gap-2">
                        {Object.entries(data.mugshot_of_day.emoji_counts || {})
                          .sort((a, b) => b[1] - a[1])
                          .map(([emoji, count]) => (
                            <span key={emoji} className="flex items-center gap-0.5 text-sm text-muted-foreground">
                              <span className="text-base leading-none">{REACTION_EMOJI[emoji]}</span>{count}
                            </span>
                          ))}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Online players + Recent activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-5 bg-gradient-card border-border">
                <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-green-400" /> Who's Online
                </h3>
                {live.online_players?.length ? (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="rta-online-list">
                    {live.online_players.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1.5 px-2 rounded-lg bg-muted/30">
                        <span className="font-medium truncate">{p.nickname}</span>
                        <span className="flex items-center gap-2 text-xs">
                          <span className={`px-2 py-0.5 rounded-full ${p.page === 'Playing' ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>{p.page}</span>
                          <span className="text-muted-foreground w-12 text-right">{relTime(p.idle_seconds)}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-6 text-center">No one online right now.</p>
                )}
              </Card>

              <Card className="p-5 bg-gradient-card border-border">
                <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" /> Recent Activity
                </h3>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="rta-recent">
                  {data.recent_activity.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm py-1.5 px-2 rounded-lg bg-muted/20">
                      <BirdAvatar avatarId={r.avatar_id} size={24} />
                      <span className="font-medium truncate flex-1">{r.nickname}</span>
                      {r.jailbird_bonus_won && <Flame className="w-3.5 h-3.5 text-amber-400" />}
                      <span className="text-primary font-semibold tabular-nums">{r.score}</span>
                      <span className="text-muted-foreground text-xs w-14 text-right">{relTime(r.seconds_ago)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Retention & engagement */}
            <div>
              <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-cyan-400" /> Retention & Engagement
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Metric icon={UserPlus} label="New Players Today" value={today.new_players} accent="text-green-400" />
                <Metric icon={Users} label="Returning Today" value={today.returning_players} accent="text-cyan-400" />
                <Metric icon={Activity} label="Next-Day Retention" value={`${ret.next_day_retention}%`} sub={`of ${ret.players_yesterday} players yesterday`} accent="text-violet-400" />
                <Metric icon={Users} label="Weekly Actives" value={ret.wau} sub="last 7 days" accent="text-blue-400" />
                <Metric icon={Gamepad2} label="Avg Games / Player" value={ret.avg_games_per_player} accent="text-amber-400" />
                <Metric icon={Target} label="Accuracy Today" value={`${today.accuracy}%`} accent="text-green-400" />
                <Metric icon={Zap} label="Bonuses Today" value={today.bonuses} accent="text-gold" />
                <Metric icon={Users} label="Total Players" value={ret.total_players_all_time} accent="text-primary" />
              </div>
            </div>

            {/* Games per hour + Top players */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-5 bg-gradient-card border-border">
                <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" /> Games / Hour (last 24h)
                </h3>
                <div className="h-48">
                  {hourly.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourly} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                        <XAxis dataKey="hour" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} interval={2} />
                        <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} contentStyle={{ background: '#0b0f17', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }} labelFormatter={(h) => `${h}:00`} />
                        <Bar dataKey="games" radius={[4, 4, 0, 0]}>
                          {hourly.map((h, i) => (
                            <Cell key={i} fill={h.games >= maxHour ? '#22d3ee' : '#3b82f6'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-16">No games in the last 24h.</p>
                  )}
                </div>
              </Card>

              <Card className="p-5 bg-gradient-card border-border">
                <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-gold" /> Top Players Today
                </h3>
                {data.top_players_today?.length ? (
                  <div className="space-y-2">
                    {data.top_players_today.map((p, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm py-1.5 px-2 rounded-lg bg-muted/20">
                        <span className="w-5 text-center font-bold text-muted-foreground">{i + 1}</span>
                        <BirdAvatar avatarId={p.avatar_id} size={26} />
                        <span className="font-medium truncate flex-1">{p.nickname}</span>
                        <span className="text-xs text-muted-foreground">{p.games} games</span>
                        <span className="text-primary font-semibold tabular-nums w-16 text-right">{p.points?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-6 text-center">No games played today yet.</p>
                )}
              </Card>
            </div>

            {/* 7-Day Trend + Community Reactions */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-5 bg-gradient-card border-border">
                <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-cyan-400" /> 7-Day Trend
                </h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data.daily_trend || []} margin={{ top: 5, right: 5, left: -22, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} />
                      <YAxis stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#0b0f17', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="games" name="Games" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="players" name="Active Players" stroke="#a78bfa" strokeWidth={2} dot={{ r: 2 }} />
                      <Line type="monotone" dataKey="new_players" name="New Players" stroke="#22d3ee" strokeWidth={2} dot={{ r: 2 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="p-5 bg-gradient-card border-border">
                <h3 className="font-heading font-bold mb-3 flex items-center gap-2">
                  <Flame className="w-4 h-4 text-orange-400" /> Community Reactions
                </h3>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-muted/30 text-center">
                    <div className="text-2xl font-heading font-bold tabular-nums" data-testid="rta-reactions-total">{(data.reactions?.total || 0).toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">Total Reactions</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30 text-center">
                    <div className="text-2xl font-heading font-bold tabular-nums text-orange-400">{(data.reactions?.today || 0).toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">Today</div>
                  </div>
                </div>
                <div className="space-y-2" data-testid="rta-reaction-breakdown">
                  {Object.keys(REACTION_EMOJI).map((key) => {
                    const rb = data.reactions?.breakdown || {};
                    const count = rb[key] || 0;
                    const maxR = Math.max(1, ...Object.values(rb));
                    const pct = Math.round((count / maxR) * 100);
                    return (
                      <div key={key} className="flex items-center gap-2 text-sm">
                        <span className="text-lg w-6 text-center">{REACTION_EMOJI[key]}</span>
                        <div className="flex-1 h-5 rounded-full bg-muted/40 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-orange-500/70 to-amber-400/70" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-12 text-right font-semibold tabular-nums text-muted-foreground">{count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RTAPage;
